import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./TopBar.css";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonOutline from "@mui/icons-material/PersonOutline";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIcon from "@mui/icons-material/Phone";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTheme } from "../../contexts/ThemeContext";

export default function Topbar({ currentUser, onLogout }) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const notifRef   = useRef();
  const profileRef = useRef();
  const quickRef   = useRef();

  const [userProfile, setUserProfile] = useState({
    firstName: 'User',
    lastName: '',
    email: '',
    initials: 'U',
  });

  useEffect(() => {
    const loadProfileData = () => {
      const savedImage = localStorage.getItem('userProfileImage');
      setProfileImage(savedImage);

      if (currentUser) {
        const nameParts = (currentUser.name || '').split(' ');
        const firstName = nameParts[0] || currentUser.name;
        const lastName  = nameParts.slice(1).join(' ') || '';
        setUserProfile({
          firstName,
          lastName,
          email: currentUser.email || '',
          initials: (currentUser.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
        });
        if (currentUser.avatar && !savedImage) setProfileImage(currentUser.avatar);
      } else {
        const saved = localStorage.getItem('userSettings');
        if (saved) {
          const p = JSON.parse(saved);
          setUserProfile({
            firstName: p.firstName,
            lastName:  p.lastName,
            email:     p.email,
            initials:  `${p.firstName.charAt(0)}${p.lastName.charAt(0)}`,
          });
        }
      }
    };

    loadProfileData();
    window.addEventListener('profileImageUpdated', loadProfileData);
    return () => window.removeEventListener('profileImageUpdated', loadProfileData);
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current   && !notifRef.current.contains(e.target))   setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (quickRef.current   && !quickRef.current.contains(e.target))   setQuickOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':    return '#dc2626';
      case 'admin':         return '#2563eb';
      case 'account_owner': return '#7c3aed';
      case 'agent':         return '#059669';
      default:              return '#6b7280';
    }
  };

  const getRoleLabel = (role) => {
    if (!role) return null;
    return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const notifications = [
    { id: 1, type: 'task',  title: 'New task assigned',       time: '2m ago',    desc: 'Complete client proposal.'  },
    { id: 2, type: 'email', title: 'New email from John',      time: '10m ago',   desc: 'Check updated document.'    },
    { id: 3, type: 'deal',  title: 'Deal closed successfully', time: '1h ago',    desc: 'Pipeline updated.'          },
    { id: 4, type: 'sms',   title: 'SMS reminder received',    time: '3h ago',    desc: 'Follow up with client.'     },
    { id: 5, type: 'call',  title: 'Missed call from client',  time: 'Yesterday', desc: 'Call back ASAP.'            },
  ];

  const getNotifIcon = (type) => {
    const map = { task: <TaskAltIcon />, email: <EmailIcon />, deal: <MonetizationOnIcon />, sms: <SmsIcon />, call: <PhoneIcon /> };
    return map[type] || <NotificationsNoneIcon />;
  };

  const Avatar = ({ size = 36 }) => (
    <div className="avatar-initials" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {profileImage
        ? <img src={profileImage} alt="Profile" className="avatar-uploaded-image" />
        : userProfile.initials}
    </div>
  );

  return (
    <div className="topbar">

      <div className="topbar-search">
        <SearchIcon className="icon" />
        <input type="text" placeholder="Search contacts, properties..." />
      </div>

      <div className="topbar-right">

        <div className="icon-btn" onClick={toggleTheme} title="Toggle theme">
          {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </div>

        <div className="icon-btn" ref={quickRef} onClick={() => setQuickOpen(o => !o)}>
          <span className="material-symbols-outlined">add</span>
          {quickOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-item" onClick={() => { setQuickOpen(false); navigate('/contacts'); }}>
                <PersonOutline /> Add Contact
              </div>
              <div className="dropdown-item"><NoteAddIcon /> Add Note</div>
              <div className="dropdown-item"><TaskAltIcon /> Add Task</div>
              <div className="dropdown-item"><EmailIcon /> Send Email</div>
              <div className="dropdown-item"><SmsIcon /> Send SMS</div>
              <div className="dropdown-item"><PhoneIcon /> Log Call</div>
              <div className="dropdown-item"><MonetizationOnIcon /> Add Deal</div>
            </div>
          )}
        </div>

        <div className="icon-btn" title="AI Assistant"><SmartToyIcon /></div>

        <div className="icon-btn notif-wrapper" ref={notifRef} onClick={() => setNotifOpen(o => !o)}>
          <NotificationsNoneIcon />
          <span className="notif-dot" />
          {notifOpen && (
            <div className="dropdown-menu notifications-menu">
              <div className="notif-header">
                Notifications
                <span className="notif-badge">{notifications.length}</span>
              </div>
              {notifications.map(n => (
                <div key={n.id} className="dropdown-item notification-item">
                  <span className="notif-icon">{getNotifIcon(n.type)}</span>
                  <div className="notif-text">
                    <span className="notif-title">{n.title}</span>
                    <span className="notif-desc">{n.desc}</span>
                    <span className="notif-time">{n.time}</span>
                  </div>
                </div>
              ))}
              <div className="notif-footer" onClick={() => { setNotifOpen(false); navigate('/profile/notifications'); }}>
                View all notifications
              </div>
            </div>
          )}
        </div>

        <div className="profile-wrapper" ref={profileRef} onClick={() => setProfileOpen(o => !o)}>
          <Avatar />
          <span className={`material-symbols-outlined arrow ${profileOpen ? 'rotate' : ''}`}>expand_more</span>

          {profileOpen && (
            <div className="dropdown-menu profile-menu" onClick={e => e.stopPropagation()}>

              <div className="profile-menu-header">
                <Avatar size={44} />
                <div className="profile-menu-info">
                  <strong>{userProfile.firstName} {userProfile.lastName}</strong>
                  <small>{userProfile.email}</small>
                  {currentUser?.role && (
                    <span className="profile-menu-role" style={{
                      color: getRoleColor(currentUser.role),
                      background: `${getRoleColor(currentUser.role)}15`,
                    }}>
                      {getRoleLabel(currentUser.role)}
                    </span>
                  )}
                </div>
              </div>

              <div className="profile-menu-divider" />

              <div className="profile-menu-section-label">Settings</div>
              <div className="dropdown-item" onClick={() => { setProfileOpen(false); window.location.href = '/admin'; }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>admin_panel_settings</span> Admin
              </div>

              <div className="profile-menu-divider" />

              <div className="profile-menu-section-label">Account</div>
              <div className="dropdown-item" onClick={() => { setProfileOpen(false); navigate('/profile/edit'); }}>
                <PersonOutline /> Edit Profile
              </div>
              <div className="dropdown-item" onClick={() => { setProfileOpen(false); navigate('/profile/notifications'); }}>
                <NotificationsNoneIcon /> Notification Preferences
              </div>

              <div className="profile-menu-divider" />

              <div className="profile-menu-section-label">Support</div>
              <div className="dropdown-item" onClick={() => { setProfileOpen(false); navigate('/support'); }}>
                <HelpOutlineIcon /> Help &amp; Support
              </div>
              <div className="dropdown-item" onClick={() => { setProfileOpen(false); window.open('/tutorials', '_blank'); }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>play_circle</span> Tutorials
              </div>

              <div className="profile-menu-divider" />

              <div className="dropdown-item logout" onClick={() => { setProfileOpen(false); if (onLogout) onLogout(); }}>
                <LogoutIcon /> Logout
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  );
}